import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  Chip,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import type { UIEvent } from "react";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDebounce } from "use-debounce";
import { useInfiniteScroll } from "../../common/hooks/useInfiniteScroll";
import { toProviderOption } from "../../common/hooks/useNavbarProviderDefaults";
import { isNurseUser } from "../../common/utils/breakGlass";
import { RootState } from "../../redux/store/store";
import { useProviderControllerServiceGetAllProviders } from "../../services/queries";

import { type NavbarProviderOption } from "../../redux/slices/ehr/navbarProvider.slice";

export type ProviderOption = NavbarProviderOption;

type ProviderMultiSelectProps = {
  value: ProviderOption[];
  onChange: (providers: ProviderOption[]) => void;
};

const ProviderMultiSelect = ({
  value: selectedProviders,
  onChange,
}: ProviderMultiSelectProps) => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch] = useDebounce(searchText, 300);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { currentLocation, user } = useSelector(
    (state: RootState) => state?.auth,
  );

  // A clinic user is never a provider, so nothing about them may be pinned
  // into the selection — their default set is handled by the navbar instead.
  const isProviderUser =
    Boolean(user?.role?.includes("provider")) &&
    user?.roleType !== "CLINIC_USER";
  const loggedInProviderUuid = isProviderUser && user?.uuid ? user.uuid : null;

  // A nurse sends their own user uuid as the care team filter. Exact match,
  // so a provider titled e.g. "Nurse Practitioner" is not treated as one.
  const isNurse = isNurseUser(user);
  const careTeamUserUuid = isNurse ? user?.userId : undefined;

  const {
    options: providerOptions,
    handleScroll,
    isFetching,
  } = useInfiniteScroll(
    (page: number) =>
      useProviderControllerServiceGetAllProviders({
        page,
        pageSize: 25,
        searchString: debouncedSearch || undefined,
        locationUuids: currentLocation ? [currentLocation] : undefined,
        careTeamUserUuid: careTeamUserUuid || undefined,
        status: true,
      }),
    // Location belongs here too: the provider set is scoped to it, so the
    // accumulated pages have to be dropped when the user switches location.
    [debouncedSearch, currentLocation, careTeamUserUuid],
    0,
    "uuid",
  );

  // A nurse's care team, picked for her by the navbar defaults, cannot be
  // deselected. It is persisted, so it is locked from the first render after
  // a reload, and it only counts for the location it was loaded for.
  const nurseDefaultsLocation = useSelector(
    (state: RootState) => state.auth.persistedNurseDefaultsLocation ?? null,
  );
  const nurseCareTeam = useSelector(
    (state: RootState) => state.auth.persistedNurseCareTeam,
  );
  const lockedCareTeamUuids = useMemo(
    () =>
      new Set(
        isNurse && nurseDefaultsLocation === currentLocation
          ? (nurseCareTeam ?? []).map((provider) => provider.uuid)
          : [],
      ),
    [isNurse, nurseDefaultsLocation, currentLocation, nurseCareTeam],
  );

  // The logged-in provider, and a nurse's care-team providers, cannot be
  // deselected.
  const isLockedProvider = (option: ProviderOption) =>
    (Boolean(loggedInProviderUuid) && option.uuid === loggedInProviderUuid) ||
    lockedCareTeamUuids.has(option.uuid);

  const formattedOptions = useMemo<ProviderOption[]>(
    () =>
      (providerOptions || [])
        .map(toProviderOption)
        .filter((provider: ProviderOption) => Boolean(provider.uuid)),
    [providerOptions],
  );

  return (
    <Autocomplete
      multiple
      size="small"
      open={isDropdownOpen}
      options={formattedOptions}
      value={selectedProviders}
      inputValue={searchText}
      disableCloseOnSelect
      filterOptions={(x) => x}
      limitTags={2}
      isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
      getOptionLabel={(option) => option.label || ""}
      onOpen={() => {
        setIsDropdownOpen(true);
      }}
      onClose={(_event, reason) => {
        if (
          reason === "blur" ||
          reason === "escape" ||
          reason === "toggleInput"
        ) {
          setIsDropdownOpen(false);
        }
      }}
      onChange={(_event, value) => {
        // The logged-in provider is always part of the filter via the base
        // list, so nothing needs pinning here — this is purely their picks.
        // A nurse's locked care-team picks are put back if a chip delete,
        // backspace or the clear button removed them.
        const keptLocked = selectedProviders.filter(
          (provider) =>
            isLockedProvider(provider) &&
            !value.some((picked) => picked.uuid === provider.uuid),
        );
        onChange([...keptLocked, ...value]);
        setSearchText("");
      }}
      renderTags={(tagValue, getTagProps) => {
        const visibleTags = tagValue.slice(0, 1);
        const hiddenCount = Math.max(tagValue.length - visibleTags.length, 0);
        const hiddenNames = tagValue
          .slice(1)
          .map((option) => option.label)
          .join(", ");

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: 0.75,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {visibleTags.map((option, index) => {
              const tagProps = getTagProps({ index });
              const locked = isLockedProvider(option);
              return (
                <Chip
                  {...tagProps}
                  key={option.uuid}
                  onDelete={locked ? undefined : tagProps.onDelete}
                  avatar={
                    <Avatar
                      src={option.avatar}
                      sx={{ width: 18, height: 18, fontSize: 10 }}
                    >
                      {option.label
                        .split(" ")
                        .map((part) => part?.charAt(0) || "")
                        .join("")
                        .slice(0, 2)
                        .toUpperCase() || "P"}
                    </Avatar>
                  }
                  label={option.label}
                  size="small"
                  deleteIcon={<CloseIcon sx={{ color: "#FFFFFF" }} />}
                  sx={{
                    maxWidth: 132,
                    height: 24,
                    bgcolor: "#2F4853",
                    color: "#FFF",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiChip-deleteIcon": {
                      color: "#FFFFFF !important",
                      opacity: 1,
                    },
                    "& .MuiChip-deleteIcon:hover": {
                      color: "#FFFFFF !important",
                    },
                  }}
                />
              );
            })}

            {hiddenCount > 0 && (
              <Tooltip
                title={hiddenNames || `${hiddenCount} more providers selected`}
                arrow
                placement="top"
              >
                <Chip
                  icon={
                    <AddIcon
                      sx={{ fontSize: 14, color: "inherit !important" }}
                    />
                  }
                  label={`+${hiddenCount}`}
                  size="small"
                  sx={{
                    height: 24,
                    minWidth: 52,
                    bgcolor: "#405A66",
                    color: "#FFF",
                    fontWeight: 700,
                    flexShrink: 0,
                    "& .MuiChip-label": {
                      px: 0.75,
                    },
                    "& .MuiChip-icon": {
                      color: "inherit",
                      ml: 0.5,
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      }}
      onInputChange={(_event, value, reason) => {
        if (reason !== "reset") {
          setSearchText(value);
          if (!isDropdownOpen) {
            setIsDropdownOpen(true);
          }
        }
      }}
      ListboxProps={{
        onScroll: (event: UIEvent<HTMLUListElement>) => {
          const list = event.currentTarget;
          if (list.scrollTop + list.clientHeight >= list.scrollHeight - 10) {
            handleScroll?.();
          }
        },
        style: { maxHeight: 360 },
      }}
      popupIcon={<ArrowDropDownIcon sx={{ color: "rgba(255,255,255,0.7)" }} />}
      noOptionsText={debouncedSearch ? "No providers found" : "No providers"}
      renderOption={(props, option, { selected }) => {
        const initials = option.label
          .split(" ")
          .map((part) => part?.charAt(0) || "")
          .join("")
          .slice(0, 2)
          .toUpperCase();
        const locked = isLockedProvider(option);

        return (
          <li
            {...props}
            key={option.uuid}
            onClick={(event) => {
              // Logged-in provider stays selected and can't be toggled off.
              if (locked) {
                event.preventDefault();
                event.stopPropagation();
                return;
              }
              props.onClick?.(event);
            }}
            aria-disabled={locked}
            style={{
              ...props.style,
              padding: "8px 12px",
              cursor: locked ? "default" : "pointer",
              opacity: locked ? 0.7 : 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 1.5,
              }}
            >
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected || locked}
                disabled={locked}
                sx={{ color: "#B0BEC5", p: 0 }}
              />
              <Avatar
                src={option.avatar}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "#2F4853",
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {initials || "P"}
              </Avatar>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{ fontSize: "13px" }}
                >
                  {option.label}
                </Typography>
                {option.specialty && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: "11px" }}
                  >
                    {option.specialty}
                  </Typography>
                )}
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selectedProviders.length > 0 ? "" : "Select Providers"}
          inputProps={{
            ...params.inputProps,
            placeholder: selectedProviders.length > 0 ? "" : "Select Providers",
            onBlur: () => {
              setIsDropdownOpen(false);
            },
          }}
          sx={{
            width: { xs: 150, sm: 160, md: 170, lg: 190, xl: 240 },
            "& .MuiOutlinedInput-root": {
              height: 36,
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: "8px",
              color: "#FFF",
              paddingRight: "4px !important",
              flexWrap: "nowrap",
              overflow: "hidden",
              "& fieldset": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.4)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(255,255,255,0.6)",
              },
            },
            "& .MuiAutocomplete-inputRoot": {
              flexWrap: "nowrap",
              overflow: "hidden",
            },
            "& .MuiAutocomplete-input": {
              minWidth: 1,
              width: selectedProviders.length > 0 ? 1 : "auto",
              opacity: selectedProviders.length > 0 ? 0 : 1,
              caretColor: "transparent",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.6)",
              opacity: 1,
            },
            "& .MuiAutocomplete-tag": {
              maxWidth: 132,
              margin: 0,
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: params.InputProps.startAdornment,
            endAdornment: (
              <>
                {isFetching && (
                  <CircularProgress
                    size={16}
                    sx={{ color: "rgba(255,255,255,0.6)", mr: 1 }}
                  />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProviderMultiSelect;
